import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, User, FileText, Wand2, Lock, Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/contexts/useCompany';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { QuotePickerDialog } from '@/components/documents/QuotePickerDialog';
import { onlyDigits } from '@/lib/documentUtils';
import {
  useCreateContract,
  useUpdateContract,
  useContract,
  isContentLocked,
  contractStatusLabel,
  type ContractInput,
  type ContractStatus,
} from '@/hooks/useContracts';
import { CONTRACT_TEMPLATES, getContractTemplate } from '@/lib/contractTemplates';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import type { Client } from '@/types/database';

/**
 * PÁGINA · Contrato (criar e editar).
 *
 * Era um `Dialog` (`ContractFormModal`) aberto de três lugares. Virou rota pelo
 * mesmo motivo da OS: o desenho oficial do app é layout de PÁGINA (hero sangrado
 * + cartões + barra flutuante ancorada na navegação inferior). Aqui o ganho é
 * ainda maior — o corpo do contrato é um `Textarea` de 12 linhas que disputava
 * altura com o `max-h-[90vh]` do diálogo.
 *
 *  · `/contratos/novo`                → em branco
 *  · `/contratos/novo?orcamento=<id>` → semeia do orçamento
 *  · `/contratos/:id/editar`          → edita (carrega pelo id)
 */

const parseCurrency = (v: string): number => {
  const n = Number(String(v).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export default function ContractFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialQuoteId = searchParams.get('orcamento');

  const { activeCompany } = useCompany();
  const isEditing = Boolean(id);

  const { contract, isLoading: loadingContract } = useContract(id);
  const contentLocked = isEditing && isContentLocked(contract?.status);

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();

  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientDocument, setClientDocument] = useState('');
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [total, setTotal] = useState('');
  const [templateId, setTemplateId] = useState(CONTRACT_TEMPLATES[0].id);
  const [content, setContent] = useState('');

  /**
   * Hidrata UMA vez, quando o contrato chega. O modal dependia de `open`; aqui a
   * página nasce montada e o dado vem depois — sem o marcador, cada refetch da
   * lista reidrataria o formulário por cima do que o usuário digitou.
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEditing || hydratedRef.current || !contract) return;
    hydratedRef.current = true;

    setClientId(contract.client_id ?? null);
    setClientName(contract.client_name ?? '');
    setClientPhone(contract.client_phone ?? '');
    setClientAddress(contract.client_address ?? '');
    setClientDocument(contract.client_document ?? '');
    setQuoteId(contract.quote_id ?? null);
    setTitle(contract.title ?? '');
    setServiceDescription('');
    setTotal(contract.total != null ? String(contract.total) : '');
    setContent(contract.content ?? '');
  }, [isEditing, contract]);

  // Fills the form from a quote + a composed service description. Shared by the
  // manual picker and the `?orcamento=` seed path so both stay in sync.
  const applyQuoteSeed = (quote: any, description: string) => {
    setQuoteId(quote.id);
    setClientId(quote.client_id ?? null);
    setClientName(quote.client_name ?? '');
    setClientPhone(quote.client_phone ?? '');
    setClientAddress(quote.client_address ?? '');
    setClientDocument(quote.client_document ?? '');
    if (quote.title) setTitle(quote.title);
    if (description) setServiceDescription(description);
    setTotal(quote.total != null ? String(quote.total) : '');
  };

  /** Descrição do serviço a partir dos nomes dos itens do orçamento. */
  const describeQuote = (quote: any, items: any[]): string => {
    const names = (items ?? []).map((i: any) => i.name).filter(Boolean).join(', ');
    return names || quote.title || '';
  };

  // Semeia o formulário de CRIAÇÃO a partir do orçamento da URL. Roda uma vez.
  const seededRef = useRef(false);
  useEffect(() => {
    if (isEditing || seededRef.current || !initialQuoteId) return;
    seededRef.current = true;

    let cancelled = false;
    (async () => {
      const { data: quote } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', initialQuoteId)
        .maybeSingle();
      if (cancelled || !quote) return;
      const { data: items } = await supabase
        .from('quote_items')
        .select('name')
        .eq('quote_id', initialQuoteId)
        .order('position', { ascending: true });
      if (cancelled) return;
      applyQuoteSeed(quote, describeQuote(quote, (items ?? []) as any[]));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, initialQuoteId]);

  const handleSelectClient = (client: Client) => {
    setClientId(client.id);
    setClientName(client.name ?? '');
    setClientPhone(client.phone ?? '');
    setClientAddress(client.address ?? '');
    setClientDocument((client as any).document ?? '');
  };

  const applyTemplate = () => {
    const tpl = getContractTemplate(templateId);
    if (!tpl) return;
    if (content.trim() && !window.confirm('Isto substitui o texto atual do contrato. Continuar?')) {
      return;
    }
    const company = activeCompany as any;
    setContent(
      tpl.build({
        providerName: company?.name ?? '',
        providerDocument: company?.cnpj_cpf ?? null,
        providerAddress: company?.address ?? null,
        clientName,
        clientDocument,
        clientAddress,
        serviceDescription: serviceDescription || title,
        total: parseCurrency(total),
        date: new Date().toISOString(),
        city: company?.pix_city ?? null,
      }),
    );
  };

  const handleSubmit = async () => {
    if (!clientName.trim() && !title.trim()) {
      toast.error('Informe ao menos um cliente ou um título para o contrato.');
      return;
    }
    if (!content.trim()) {
      toast.error('Escreva ou gere o corpo do contrato.');
      return;
    }

    const payload: ContractInput = {
      quote_id: quoteId,
      client_id: clientId,
      client_name: clientName.trim() || null,
      client_phone: clientPhone.trim() || null,
      client_address: clientAddress.trim() || null,
      // Store digits only (convention across clients/quotes; the CPF challenge
      // normalizes anyway). Keeps client_document consistent app-wide.
      client_document: onlyDigits(clientDocument) || null,
      title: title.trim() || null,
      content: content,
      total: parseCurrency(total),
    };

    try {
      if (isEditing) {
        await updateContract.mutateAsync({
          id: contract.id,
          currentStatus: contract.status,
          updates: {
            client_id: payload.client_id,
            client_name: payload.client_name,
            client_phone: payload.client_phone,
            client_address: payload.client_address,
            client_document: payload.client_document,
            title: payload.title,
            total: payload.total,
            // content only lands when not locked (guarded again in the hook).
            ...(contentLocked ? {} : { content: payload.content }),
          },
        });
        toast.success('Contrato atualizado.');
      } else {
        const res = await createContract.mutateAsync(payload);
        toast.success((res as any)?.offline ? 'Contrato salvo localmente.' : 'Contrato criado.');
      }
      navigate(-1);
    } catch {
      // toast handled in the mutation onError
    }
  };

  const saving = createContract.isPending || updateContract.isPending;

  if (isEditing && !contract && loadingContract) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          <ContractHero isEditing total={0} clientName="" status={null} onBack={() => navigate(-1)} />
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (isEditing && !contract) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">Contrato não encontrado</h1>
          <Button3D className="mt-6" onClick={() => navigate('/contratos')}>
            Ver contratos
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        <ContractHero
          isEditing={isEditing}
          total={parseCurrency(total)}
          clientName={clientName}
          status={(contract?.status ?? null) as ContractStatus | null}
          onBack={() => navigate(-1)}
        />

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-3">
          {!isEditing && (
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full justify-start rounded-2xl bg-card"
              onClick={() => setShowQuotePicker(true)}
            >
              <FileText className="mr-2 h-4 w-4 text-primary" />
              Criar a partir de um orçamento
            </Button>
          )}

          {/* CONTRATANTE */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel icon={User} htmlFor="contract-client">
                Cliente (contratante)
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="contract-client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Nome do cliente"
                  className={FIELD_INPUT}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={() => setShowClientSelector(true)}
                  aria-label="Escolher cliente do cadastro"
                >
                  <User className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="contract-phone">Telefone</FieldLabel>
                <Input
                  id="contract-phone"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  className={FIELD_INPUT}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="contract-document">CPF/CNPJ</FieldLabel>
                <Input
                  id="contract-document"
                  value={clientDocument}
                  onChange={(e) => setClientDocument(e.target.value)}
                  placeholder="Documento"
                  className={FIELD_INPUT}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              O CPF/CNPJ é usado como desafio de identidade na assinatura remota. Informe-o antes de
              enviar o contrato.
            </p>
          </div>

          {/* OBJETO */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel icon={Scale} htmlFor="contract-title">
                Título
              </FieldLabel>
              <Input
                id="contract-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Contrato de instalação elétrica"
                className={FIELD_INPUT}
              />
            </div>
            {/* Valor e objeto EMPILHADOS: lado a lado, "Serviço (resumo)" fica com
                menos da metade da linha para um texto livre que alimenta o
                modelo — e some no `truncate` sem o usuário perceber. */}
            <div className="space-y-1.5">
              <FieldLabel htmlFor="contract-total">Valor (R$)</FieldLabel>
              <Input
                id="contract-total"
                inputMode="decimal"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="0,00"
                className={FIELD_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="contract-service">Serviço (resumo)</FieldLabel>
              <Input
                id="contract-service"
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                placeholder="Objeto do contrato"
                className={FIELD_INPUT}
              />
            </div>
          </div>

          {/* MODELO + CORPO */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            {/* Template selector — disabled once the body is locked (sent). */}
            {!contentLocked && (
              <div className="space-y-1.5">
                <FieldLabel htmlFor="contract-template">Modelo</FieldLabel>
                <div className="flex gap-2">
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger id="contract-template" className={cn(FIELD_INPUT, 'flex-1')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_TEMPLATES.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={applyTemplate}>
                    <Wand2 className="mr-1.5 h-4 w-4" />
                    Gerar
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {getContractTemplate(templateId)?.description}
                </p>
              </div>
            )}

            {/* Contract body */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel htmlFor="contract-content">Corpo do contrato</FieldLabel>
                {contentLocked && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" /> bloqueado após envio
                  </span>
                )}
              </div>
              <Textarea
                id="contract-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escreva ou gere o texto do contrato a partir de um modelo."
                rows={14}
                disabled={contentLocked}
                className="rounded-xl bg-background font-mono text-xs leading-relaxed"
              />
            </div>
          </div>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página. */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D
              color="gray"
              size="sm"
              className="flex-1"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancelar
            </Button3D>
            <Button3D size="sm" className="flex-1" onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Salvando…
                </>
              ) : isEditing ? (
                'Salvar'
              ) : (
                'Criar contrato'
              )}
            </Button3D>
          </div>
        </div>
      </div>

      <ClientSelector open={showClientSelector} onOpenChange={setShowClientSelector} onSelect={handleSelectClient} />
      <QuotePickerDialog
        open={showQuotePicker}
        onOpenChange={setShowQuotePicker}
        companyId={activeCompany?.id ?? null}
        onPick={(quote, items) => {
          applyQuoteSeed(quote, describeQuote(quote, items));
          setShowQuotePicker(false);
        }}
      />
    </MainLayout>
  );
}

/**
 * HERO do contrato. O contrato é um DOCUMENTO com valor, então o valor ao vivo é
 * o TOTAL (como no orçamento e na OS).
 *
 * NÃO há pílulas de status aqui, e isso é deliberado: o status do contrato não é
 * escolhido no formulário — ele avança por AÇÕES (enviar, assinar, cancelar), e
 * o cliente participa disso pela página pública. Oferecer as pílulas sugeriria
 * que dá para marcar "assinado" na mão, que é justamente o que a
 * evidência-de-adulteração do módulo existe para impedir. Em edição, o status
 * aparece como SELO — informação, não controle.
 */
function ContractHero({
  isEditing,
  total,
  clientName,
  status,
  onBack,
}: {
  isEditing: boolean;
  total: number;
  clientName: string;
  status: ContractStatus | null;
  onBack: () => void;
}) {
  const formatted = formatBRL(total);
  return (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={onBack}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEditing ? 'Editando' : 'Novo'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Contrato</h1>
        </div>
        {status && (
          <div className="shrink-0 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium">
            {contractStatusLabel(status)}
          </div>
        )}
      </div>

      <div className="relative mt-5">
        <p className="text-xs text-white/75">Valor do contrato</p>
        <p className={cn('truncate font-bold leading-tight', heroTotalSizeClass(formatted))}>
          {formatted}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {clientName.trim() || 'Preencha os dados abaixo'}
        </p>
      </div>
    </div>
  );
}
